import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { downloadReport, downloadLargeReport } from '../Dashboard.actions';
import {
	CSV_FILE_TYPE,
	MAX_ROW_COUNT,
	PROJECTS,
	SHARED,
	L_FILE_DOWNLOAD_MSG,
	REPORT_NAME,
	DRAFT_NAME,
	LAST_RUN_DATE,
	LAST_UPDATED_DATE,
	PURGE_DATE
} from 'constants/constant';
import RBSnackbar from 'components/Shared/RBSnackbar';
import ActiveReportTable from './ActiveReportTable';
import SavedReportTable from './SavedReportTable';
import SharedRecipeTable from './SharedRecipeTable';
import { sortByName, sortByDate, deepClone } from 'helper/helper';
import { trackEventInfo } from 'helper/analytics';
import { EVENTS, PAGE_ID, ACTION_ON } from 'constants/tracker';
import { UserPortalContext } from 'context/appcontext';
import RemoveAccess from '../../Share-report/RemoveAccess';

const ReportList = (props) => {
	const { isSmallScreen, mobileSearchOpen, callReportSaved, list } = props;
	const dispatch = useDispatch();
	const { tabValue, searchTxt, _sortDirection, _sortBy } = useSelector(
		(state) => state.dashboardReducer
	);
	const reportsList = useSelector((state) =>
		tabValue === PROJECTS ? state.dashboardReducer.reportsList : state.dashboardReducer.sharedList
	);
	const [reportData, setReportData] = useState(list);
	const isLargeDownload = useSelector((state) => state.dashboardReducer.isLargeDownload);
	const reportTypeTabValue = useSelector((state) => state.dashboardReducer.reportTypeTabValue);
	const sharedReportTabValue = useSelector((state) => state.dashboardReducer.sharedReportTabValue);
	const portalUserContext = useContext(UserPortalContext);
	const { userDetails } = portalUserContext;
	const { companyName, loginId } = userDetails?.result;
	const { savedReportsList } = useSelector((state) => state.dashboardReducer);
	const [shareReportShow, setShareReportShow] = useState(false);
	const [row, setRow] = useState(null);

	useEffect(() => {
		setReportData(list);
	}, [list]);

	const resetData = () => {
		if (reportTypeTabValue === 1) {
			setReportData(savedReportsList);
		} else {
			setReportData(reportsList);
		}
	};

	useEffect(() => {
		let fieldName = REPORT_NAME;
		if (searchTxt !== '') {
			if (reportTypeTabValue === 1) {
				fieldName = DRAFT_NAME;
			}
			const searchedList = list.filter((item) =>
				item[fieldName].toLowerCase().includes(searchTxt.toLowerCase())
			);
			setReportData([...searchedList]);
		} else {
			resetData();
		}
	}, [searchTxt]);

	useEffect(() => {
		const unSortedReports = deepClone(reportData);
		let sortedReports = [];
		// sorts data acc. to column & direction clicked
		if ([LAST_RUN_DATE, PURGE_DATE, LAST_UPDATED_DATE].includes(_sortBy)) {
			sortedReports = unSortedReports.sort((a, b) => sortByDate(a, b, _sortDirection, _sortBy));
		} else {
			sortedReports = unSortedReports.sort((a, b) => sortByName(a, b, _sortDirection, _sortBy));
		}

		setReportData(sortedReports);
	}, [_sortBy, _sortDirection]);

	const handleDownload = (param, index) => {
		trackEventInfo({
			type: EVENTS.CLICK,
			actionOn: tabValue === SHARED ? ACTION_ON.DOWNLOAD_SHARED_REPORT : ACTION_ON.DOWNLOAD_BUTTON,
			location: PAGE_ID.DASHBOARD
		});

		const { reportId, fileUrl, rowCount, fileType } = param;
		const downloadRequest = { reportId, fileUrl };

		dispatch(
			downloadReport({ requestBody: downloadRequest, companyName, loginId, rowCount, fileType })
		);
		if (rowCount > MAX_ROW_COUNT && fileType.toUpperCase() === CSV_FILE_TYPE) {
			dispatch(
				downloadLargeReport({ requestBody: downloadRequest, companyName, loginId, rowCount })
			);
		}
		/**
		 * We need to update the downloadCount of param itself which will update the count in MobileTable and CustomTable
		 * so that we can display appropriate option lable from DOWNLOAD and DOWNLOAD AGAIN
		 * */
		param.downloadCount = param.downloadCount + 1;

		reportData[index].downloadCount = param.downloadCount + 1;
		setReportData(reportData);
	};

	const handleShowSharedPanel = (_d) => {
		setRow(_d);
		setShareReportShow(true);
	};

	const commonProps = {
		handleDownload,
		isSmallScreen,
		callReportSaved,
		reportData,
		cbShowSharedReport: handleShowSharedPanel
	};
	const tableContent = useMemo(() => {
		const _ret = {
			[0]: <ActiveReportTable {...commonProps} />,
			[1]: <SavedReportTable {...commonProps} />,
			[2]: <SharedRecipeTable {...commonProps} />
		};

		if (tabValue === 'Shared') {
			if (sharedReportTabValue === 1) {
				return _ret[2];
			}
			return _ret[0];
		}

		return _ret[reportTypeTabValue];
	}, [tabValue, sharedReportTabValue, reportData]);

	return (
		<Box maxHeight={mobileSearchOpen ? 'calc(100vh - 200px)' : '80vh'}>
			{tableContent}
			<RBSnackbar doOpen={isLargeDownload} msg={L_FILE_DOWNLOAD_MSG} />
			<RemoveAccess data={row} show={shareReportShow} setShow={setShareReportShow} />
		</Box>
	);
};

ReportList.propTypes = {
	isSmallScreen: PropTypes.bool
};

export default ReportList;
